import { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/feature/Layout';
import WorkCard from '@/components/base/WorkCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import { useSEO } from '@/hooks/useSEO';
import { movieGenres, tvGenres, regions, years, type WorkType } from '@/mocks/works';
import { filterWorks, getWorksByType, sortByHot, sortByUpdate } from '@/utils/works';
import FilterBar, { type FilterState } from './components/FilterBar';

const PAGE_SIZE = 10;

const META: Record<WorkType, { crumb: string; heading: string; desc: string; icon: string }> = {
  tv: {
    crumb: '电视剧',
    heading: '电视剧频道',
    desc: '包含都市、悬疑、历史、古装等多种题材精选剧集，支持按类型、地区、年份筛选。',
    icon: 'ri-tv-2-line',
  },
  movie: {
    crumb: '电影',
    heading: '电影频道',
    desc: '汇集科幻、剧情、动作、爱情等热门电影作品，支持多维度筛选与排序。',
    icon: 'ri-movie-2-line',
  },
};

const DEFAULT_FILTER: FilterState = { genre: '全部', region: '全部', year: '全部', sort: 'update' };

export default function CategoryPage({ type }: { type: WorkType }) {
  const meta = META[type];
  const genres = type === 'tv' ? tvGenres : movieGenres;

  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [page, setPage] = useState(1);

  useSEO({
    title: `${meta.crumb} - 高清影视推荐 - 年轻的岳母`,
    description: `年轻的岳母${meta.crumb}频道，${meta.desc}`,
    keywords: `年轻的岳母,${meta.crumb},影视资源,${genres.slice(0, 4).join(',')}`,
  });

  const all = useMemo(() => getWorksByType(type), [type]);

  const result = useMemo(() => {
    const filtered = filterWorks(all, filter);
    return filter.sort === 'hot' ? sortByHot(filtered) : sortByUpdate(filtered);
  }, [all, filter]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const updateFilter = (next: Partial<FilterState>) => setFilter((prev) => ({ ...prev, ...next }));
  const resetFilter = () => setFilter(DEFAULT_FILTER);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.crumb }]} />
          <div className="mt-4 flex items-center gap-3">
            <span className="w-12 h-12 rounded-lg bg-primary-600 text-background-50 flex items-center justify-center text-2xl">
              <i className={meta.icon} aria-hidden="true"></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {meta.heading}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">{meta.desc}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <FilterBar
          genres={genres}
          regions={regions}
          years={years}
          value={filter}
          onChange={updateFilter}
          onReset={resetFilter}
        />

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-accent-600">{result.length}</strong> 部作品
          </p>
          <p className="text-xs text-foreground-400">
            第 {current} / {totalPages} 页
          </p>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {paged.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination
              current={current}
              total={totalPages}
              onChange={(p) => {
                setPage(p);
                scrollToTop();
              }}
            />
          </>
        ) : (
          <div className="mt-8 py-16 flex flex-col items-center text-center rounded-lg bg-background-50 border border-background-200">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有找到符合条件的作品
            </h3>
            <p className="mt-1.5 text-sm text-foreground-500">
              试试调整筛选条件，或直接重置查看全部内容
            </p>
            <button
              type="button"
              onClick={resetFilter}
              className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}